import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button } from 'antd';
import {
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { ProjectScene } from '../../../../types/avatar-video';

interface ProjectProgressModalProps {
  open: boolean;
  projectStatus: 'idle' | 'processing' | 'completed' | 'partial' | 'failed';
  projectScenes: ProjectScene[];
  totalScenes: number;
  completedScenes: number;
  onClose: () => void;
  onEdit?: () => void;
}

const STATUS_ICON: Record<string, React.ReactNode> = {
  pending: <ClockCircleOutlined style={{ fontSize: 18, color: C.textMuted }} />,
  video_processing: <LoadingOutlined style={{ fontSize: 18, color: C.warning }} />,
  completed: <CheckCircleOutlined style={{ fontSize: 18, color: C.success }} />,
  failed: <CloseCircleOutlined style={{ fontSize: 18, color: C.danger }} />
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'En cola',
  video_processing: 'Generando...',
  completed: 'Listo',
  failed: 'Error'
};

const TITLE_MAP: Record<string, string> = {
  processing: 'Generando video...',
  completed: 'Proyecto completado',
  partial: 'Completado parcialmente',
  failed: 'Error en la generación'
};

export const ProjectProgressModal: React.FC<ProjectProgressModalProps> = ({
  open,
  projectStatus,
  projectScenes,
  totalScenes,
  completedScenes,
  onClose,
  onEdit
}) => {
  const { spacing, fontSize } = useResponsiveSizes();
  const [countdown, setCountdown] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const shouldRedirect = onEdit && completedScenes > 0 && projectStatus === 'completed';

  useEffect(() => {
    if (!shouldRedirect) {
      setCountdown(null);
      return;
    }

    setCountdown(3);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          onEdit!();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [shouldRedirect, onEdit]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      title={null}
      closable={projectStatus !== 'processing'}
      maskClosable={projectStatus !== 'processing'}
      styles={{
        body: { padding: 0 },
        content: { borderRadius: 16, overflow: 'hidden' }
      }}
    >
      <div
        style={{
          padding: `${spacing.lg}px ${spacing.lg}px ${spacing.md}px`,
          borderBottom: `1px solid ${C.border}`
        }}
      >
        <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: C.textPrimary }}>
          {TITLE_MAP[projectStatus] || 'Progreso del proyecto'}
        </div>
        {totalScenes > 0 && (
          <div style={{ fontSize: fontSize.sm, color: C.textSecondary, marginTop: 4 }}>
            {completedScenes} de {totalScenes} {totalScenes === 1 ? 'escena lista' : 'escenas listas'}
          </div>
        )}
        {projectStatus === 'processing' && (
          <div
            style={{
              fontSize: fontSize.sm,
              color: C.textMuted,
              marginTop: spacing.xs,
              fontStyle: 'italic'
            }}
          >
            Esto puede demorar unos minutos...
          </div>
        )}
      </div>

      <div style={{ padding: `${spacing.md}px ${spacing.lg}px`, maxHeight: 360, overflowY: 'auto' }}>
        {projectScenes.map(scene => (
          <div
            key={scene.index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `${spacing.sm}px 0`,
              borderBottom: `1px solid ${C.border}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              {STATUS_ICON[scene.status] || STATUS_ICON.pending}
              <div>
                <div style={{ fontSize: fontSize.base, fontWeight: 500, color: C.textPrimary }}>
                  Escena {String(scene.index + 1).padStart(2, '0')}
                </div>
                <div style={{ fontSize: fontSize.sm, color: C.textMuted }}>
                  {STATUS_LABEL[scene.status] || 'Pendiente'}
                </div>
              </div>
            </div>

            {scene.status === 'failed' && scene.errorMessage && (
              <span style={{ fontSize: fontSize.sm, color: C.danger, maxWidth: 160, textAlign: 'right' }}>
                {scene.errorMessage}
              </span>
            )}
          </div>
        ))}
      </div>

      {(projectStatus === 'completed' || projectStatus === 'partial' || projectStatus === 'failed') && (
        <div
          style={{
            padding: `${spacing.md}px ${spacing.lg}px`,
            borderTop: `1px solid ${C.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {shouldRedirect && countdown !== null ? (
            <span style={{ fontSize: fontSize.sm, color: C.textSecondary }}>Abriendo editor en {countdown}s…</span>
          ) : (
            <span />
          )}
          <Button type='primary' onClick={onClose}>
            Cerrar
          </Button>
        </div>
      )}
    </Modal>
  );
};
